import { Play } from "phosphor-react"
import React, { Suspense, lazy, useState } from "react"
import { TranscodingState } from "~/__generated__/graphql"
import { cn } from "~/common/cn"
import { AppText } from "~/ui/app-text"

const MuxPlayer = lazy(() => import("@mux/mux-player-react"))

interface Props {
  muxPlaybackId: string | null | undefined
  transcodingState?: TranscodingState | null
  aspectRatio?: string | null
  playable?: boolean
  className?: string
}

export const MessageAttachmentVideo: React.FC<Props> = ({
  muxPlaybackId,
  transcodingState,
  aspectRatio,
  playable = true,
  className,
}) => {
  const [playing, setPlaying] = useState(false)

  if (transcodingState && transcodingState !== TranscodingState.Completed) {
    return (
      <AppText variant="body2">
        {transcodingState === TranscodingState.Error ? "Video unavailable" : "Video processing"}
      </AppText>
    )
  }
  if (!muxPlaybackId) return <AppText variant="body2">Video unavailable</AppText>

  const ratio = parseAspectRatio(aspectRatio)
  const thumbnailUrl = `https://image.mux.com/${muxPlaybackId}/thumbnail.png`
  const thumbnail = <img src={thumbnailUrl} className="h-full w-full object-cover" />

  return (
    <div
      className={cn("relative w-full overflow-hidden rounded-md bg-black", className)}
      style={{ aspectRatio: ratio, maxWidth: ratio < 1 ? 250 : 400 }}
    >
      {playing ? (
        <Suspense fallback={thumbnail}>
          <MuxPlayer
            playbackId={muxPlaybackId}
            streamType="on-demand"
            poster={thumbnailUrl}
            autoPlay
            className="h-full w-full"
          />
        </Suspense>
      ) : (
        <>
          {thumbnail}
          <button
            type="button"
            aria-label="Play video"
            disabled={!playable}
            onClick={() => setPlaying(true)}
            className="absolute inset-0 flex items-center justify-center"
          >
            <Play size={40} className="text-base-white" weight="fill" />
          </button>
        </>
      )}
    </div>
  )
}

const parseAspectRatio = (aspectRatio: string | null | undefined): number => {
  const [width, height] = (aspectRatio || "").split(":").map((s) => parseFloat(s))
  return width > 0 && height > 0 ? width / height : 1
}
